import { useEffect, useState } from 'react';
import { message, Modal, Radio, Space } from 'antd';
import { isEqual, pick } from 'lodash-es';

import { DebounceSelect } from 'components/common';
import { classnames } from 'lib/classnames';
import { RemoveType, SMALL_MODAL_WIDTH } from 'lib/global';
import useI18n from 'lib/hooks/useI18n';
import { ItemLink, ItemLinkType } from 'lib/models';
import Parse, { escapeMatchesQueryArg } from 'lib/parse';
import { PointerObject } from 'lib/types/models';

import styles from './deleteModal.module.less';

const cx = classnames(styles);
interface DeleteModalProps {
  isVisible: boolean;
  handleDeleteOk?: (itemLink: PointerObject[], removeType: string, selectLinkType: string) => void;
  handleDeleteCancel?: () => void;
  itemLinkType: any;
}
export const getLinkTypeOptions = async (
  name: string | RegExp,
  objectId: string,
  queryBuilder: (query: Parse.Query<any>) => Parse.Query<any>,
): Promise<Record<'label' | 'value', string>[]> => {
  let queryObject = new Parse.Query(ItemLinkType);
  if (name) {
    queryObject = Parse.Query.or(new Parse.Query(ItemLinkType).matches('name', escapeMatchesQueryArg(name), 'i'));
  }
  const list = await queryBuilder(queryObject).find();
  const itemLinkTypeList = list
    .filter(ele => ele.id !== objectId)
    .map(item => {
      const itemLinkType = item.toJSON();
      return {
        ...itemLinkType,
        key: itemLinkType?.objectId,
        label: itemLinkType?.name,
        value: itemLinkType?.objectId,
      };
    });
  return itemLinkTypeList;
};

const DeleteModal: React.FC<DeleteModalProps> = ({ isVisible, handleDeleteOk, handleDeleteCancel, itemLinkType }) => {
  const { t } = useI18n();

  const [itemLink, setItemLink] = useState([]);
  const [removeType, setRemoveType] = useState(RemoveType.Reset);
  const [selectLinkType, setSelectLinkType] = useState(null);
  const [selectLinkTypeOption, setSelectLinkTypeOption] = useState(null);
  const queryBuilder = (query: Parse.Query<any>) => {
    return query.select(['name', 'key', 'createdAt']).addDescending('createdAt');
  };
  useEffect(() => {
    const getItemLink = async () => {
      const itemLink = await new Parse.Query(ItemLink).equalTo('linkType', itemLinkType.objectId).findAll();
      setItemLink(itemLink);
    };
    getItemLink();
  }, [itemLinkType]);
  const onRemoveChange = e => {
    setRemoveType(e.target.value);
  };
  const handleDelete = () => {
    if (itemLink.length) {
      if (removeType === RemoveType.Reset && !selectLinkType) {
        message.error(t('pages.itemTypesLocaleValues.default.message.3'));
        return;
      }
      if (removeType === RemoveType.Reset) {
        const pickKeys = [
          'type',
          'inwardItemType',
          'inwardItemTypeMappings',
          'outwardItemType',
          'outwardItemTypeMappings',
        ];
        const selectLinkTypeOptionPick = pick(selectLinkTypeOption, pickKeys);
        const itemTypePick = pick(itemLinkType, pickKeys);
        const isTransition = isEqual(selectLinkTypeOptionPick, itemTypePick);
        if (!isTransition) {
          message.error(t('pages.itemTypesLocaleValues.default.message.4'));
          return;
        }
      }
    }
    handleDeleteOk(itemLink, removeType, selectLinkType);
  };
  const linkTypeChange = (val, option) => {
    setSelectLinkType(val);
    setSelectLinkTypeOption(option);
  };
  return (
    <Modal
      title={`${t('pages.itemTypes.modal.delete.title')}${itemLinkType.name}`}
      open={isVisible}
      onOk={handleDelete}
      onCancel={handleDeleteCancel}
      maskClosable={false}
      width={SMALL_MODAL_WIDTH}
    >
      {itemLink.length ? (
        <div className={cx('delete-link-type')}>
          <p>
            {t('pages.itemTypes.modal.delete.text', { text: itemLink.length })}
            {/* 您确定要删除此链接类型吗？目前有<strong>{itemLink.length}</strong>个匹配的链接 */}
          </p>
          <Radio.Group className={cx('radio-group')} onChange={onRemoveChange} defaultValue={removeType}>
            <Space direction="vertical">
              <div>
                <Radio value={RemoveType.Reset}>{t('pages.itemTypes.modal.delete.reset')}</Radio>
                <DebounceSelect
                  showSearch
                  onChange={linkTypeChange}
                  placeholder={t('pages.itemTypesLocaleValues.default.placeholder')}
                  allowClear
                  fetchOptions={name => getLinkTypeOptions(name, itemLinkType.objectId, queryBuilder)}
                />
              </div>
              <Radio value={RemoveType.Remove}>{t('pages.itemTypesLocaleValues.default.remove')}</Radio>
            </Space>
          </Radio.Group>
        </div>
      ) : (
        <p className={cx('delete-tip')}>{t('pages.itemTypesLocaleValues.default.deleteTip')}</p>
      )}
    </Modal>
  );
};
export default DeleteModal;
